"use client";

import { useSession } from "@/context/SessionContext";
import { markTourCompleted } from "@/utils/serverActions/tourActions";
import { COMPANY_JUST_CREATED_KEY, isTourBlockedRoute } from "@/utils/tour-utils";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export type TourCategory = "overview" | "module";

export interface TourStep {
  id: string;
  target: string;
  secondaryTarget?: string;
  // Which highlighted element should anchor the tooltip when both exist
  // 'primary' uses target, 'secondary' uses secondaryTarget (default: 'secondary' if secondaryTarget exists)
  tooltipTarget?: "primary" | "secondary";
  title: string;
  content: string;
  details?: string; // Contenido adicional que se puede expandir
  placement?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right"
    | "left-top"
    | "left-center"
    | "left-bottom"
    | "right-top"
    | "right-center"
    | "right-bottom"
    | "center";
  action?: "click" | "hover" | "none";
  delay?: number;
  skipable?: boolean;
  isTransition?: boolean;
  nextTour?: string;
}

export interface TourDefinition {
  id: string;
  name: string;
  description: string;
  category: TourCategory;
  icon: string;
  steps: TourStep[];
  autoLaunch?: boolean;
  estimatedDuration?: string;
  route?: string; // Add route property for navigation
  hidden?: boolean;
}

export interface TourState {
  isActive: boolean;
  currentStep: number;
  steps: TourStep[];
  isCompleted: boolean;
  currentTourId?: string;
  showWelcomeModal: boolean;
  showImportIntroModal: boolean;
  showImportModal: boolean;
}

interface TourContextType {
  tourState: TourState;
  startTour: (tourId: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  resetTour: () => void;
  getAvailableTours: () => Promise<TourDefinition[]>;
  getTourProgress: (tourId: string) => boolean;
  isTourActive: () => boolean;
  isEndingTour: boolean;
  resetEndingTour: () => void;
  closeWelcomeModal: () => void;
  startTourFromWelcome: () => void;
  startAlternativeTour: () => void;
  /** "import" opens the import dialog next; otherwise the choice is done. */
  closeImportIntroModal: (next?: "import") => void;
  showImportIntroModal: () => void;
  closeImportModal: () => void;
  showImportModal: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};

interface TourProviderProps {
  children: React.ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { session, refreshSession } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [tourState, setTourState] = useState<TourState>({
    isActive: false,
    currentStep: 0,
    steps: [],
    isCompleted: false,
    showWelcomeModal: false,
    showImportIntroModal: false,
    showImportModal: false,
  });
  const [hasAutoLaunched, setHasAutoLaunched] = useState(false);
  const [completedTours, setCompletedTours] = useState<Set<string>>(new Set());
  const [isEndingTour, setIsEndingTour] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // The import onboarding was already answered in this page. The session keeps
  // the stale flag until the next fetch, so this stops it from coming back.
  const importOnboardingDoneRef = useRef(false);
  // Set when the import choice opened first (a company was just created): the
  // tour welcome is offered once that choice is done.
  const offerWelcomeAfterImportRef = useRef(false);

  // Initialize completed tours from database session data
  useEffect(() => {
    if (session.tourCompleted) {
      setCompletedTours((prev) => new Set(prev).add("overview"));
    }
  }, [session.tourCompleted]);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth < 768 ||
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Function to reset contract selection to first contract when tour ends
  const resetContractSelection = useCallback(() => {
    const currentPath = window.location.pathname;

    if (currentPath.startsWith("/service-scopes")) {
      const windowWithReset = window as unknown as {
        resetContractSelection?: () => void;
      };
      windowWithReset.resetContractSelection?.();
    } else if (currentPath.startsWith("/scheduling")) {
      const windowWithReset = window as unknown as {
        resetSchedulingContractSelection?: () => void;
      };
      windowWithReset.resetSchedulingContractSelection?.();
    }
  }, []);

  // Function to invalidate queries based on current route
  const invalidateQueriesForCurrentRoute = useCallback(() => {
    // Clear all queries that might contain dummy data
    const queryPrefixesToClear = [
      "contracts",
      "templateDemand",
      "templateDemandDetails",
      "templateShiftsList",
      "templateShiftsDetails",
      "contractDetails",
      "candidates",
      "people",
      "scheduling",
      "budgets",
      "companyTags",
      "peopleTags",
      "peopleCategories",
      "peopleFiles",
      "peopleFinancials",
      "peopleGeneral",
      "peopleEducation",
      "peopleExperience",
      "peopleInterview",
      "peopleAvailability",
      "peopleLog",
      "peopleSend",
      "peopleOther",
      "payments",
      "shiftsCyclesList",
    ];

    queryClient.removeQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if (Array.isArray(queryKey)) {
          const prefix = queryKey[0] as string;
          return queryPrefixesToClear.includes(prefix);
        }
        return false;
      },
    });

    // Remove specific dummy contract queries
    const dummyContractIds = [692, 693, 694, 695, 696];
    dummyContractIds.forEach((contractId) => {
      queryClient.removeQueries({
        queryKey: ["templateDemand", contractId],
      });
      queryClient.removeQueries({
        queryKey: ["templateShiftsList", 0, contractId],
      });
      queryClient.removeQueries({
        queryKey: ["contractDetails", contractId],
      });
    });

    // Clear cache and refetch
    queryClient.clear();
    setTimeout(() => {
      queryClient.refetchQueries();
    }, 100);

    // Reset contract selection after queries are cleared
    setTimeout(() => {
      resetContractSelection();
    }, 200);
  }, [queryClient, resetContractSelection]);

  const startTour = useCallback(
    async (tourId: string) => {
      // Disable tours on mobile devices
      if (isMobile) {
        console.log("Tours disabled on mobile devices");
        return;
      }

      // Disable tours on links routes (shared links)
      const currentPath = window.location.pathname;
      const isLinksRoute =
        currentPath.startsWith("/candidates/") ||
        currentPath.startsWith("/customers/") ||
        currentPath.startsWith("/services/") ||
        currentPath.startsWith("/view/budget/") ||
        currentPath.startsWith("/info/") ||
        currentPath.startsWith("/invalid");

      if (isLinksRoute) {
        console.log("Tours disabled on shared links routes");
        return;
      }

      try {
        // Import tour definitions dynamically to avoid circular dependencies
        const { getTourById } =
          await import("@/components/tour/tourDefinitions");
        const tourDefinition = getTourById(tourId);

        if (!tourDefinition) {
          console.error(`Tour with id "${tourId}" not found`);
          return;
        }

        const newTourState = {
          isActive: true,
          currentStep: 0,
          steps: tourDefinition.steps,
          isCompleted: false,
          currentTourId: tourId,
          showWelcomeModal: false,
        };

        // Apagamos el tour actual inmediatamente.
        // Esto desmonta el Tooltip evitando errores mientras el router cambia de página.
        setTourState((prev) => ({ ...prev, isActive: false }));

        // 2. Navegamos si es necesario.
        if (
          tourDefinition.route &&
          tourDefinition.route !== window.location.pathname
        ) {
          router.push(tourDefinition.route);
        }

        // 3. Activamos el nuevo tour INMEDIATAMENTE.
        // La lógica de esperar a que los elementos del DOM aparezcan
        // ahora será responsabilidad del componente que renderiza el tour,
        // no del contexto. Esto es mucho más robusto.
        setTourState((prev) => ({ ...prev, ...newTourState }));

        // 4. Invalidamos las consultas para que la nueva página tenga datos frescos.
        // El hook de polling se encargará de esperar si esto causa un <Suspense>.
        queryClient.invalidateQueries();
      } catch (error) {
        console.error("Error starting tour:", error);
      }
    },
    [router, isMobile, queryClient],
  );

  const nextStep = useCallback(async () => {
    setTourState((prev) => {
      if (prev.currentStep < prev.steps.length - 1) {
        const nextStepIndex = prev.currentStep + 1;
        const nextStep = prev.steps[nextStepIndex];

        // Handle employee tour specific logic
        if (prev.currentTourId === "employees") {
          const currentStepId = prev.steps[prev.currentStep]?.id;
          const nextStepId = nextStep?.id;

          // Open filter sidebar when reaching the filter sidebar step
          if (nextStepId === "employees-filter-sidebar") {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("tour-open-employees-sidebar"),
              );
            }, 100);
          }

          // Close sidebar when leaving filter sidebar step
          if (
            currentStepId === "employees-filter-sidebar" &&
            nextStepId !== "employees-filter-sidebar"
          ) {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("tour-close-employees-sidebar"),
              );
            }, 100);
          }

          // Close sidebar when leaving filter button step (unless going to sidebar)
          if (
            currentStepId === "employees-filter-button" &&
            nextStepId !== "employees-filter-sidebar"
          ) {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("tour-close-employees-sidebar"),
              );
            }, 100);
          }

          // Close tags menu when leaving tags step
          if (
            currentStepId === "employees-tags" &&
            nextStepId !== "employees-tags"
          ) {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("tour-close-employees-tags-menu"),
              );
            }, 100);
          }
        }

        return {
          ...prev,
          currentStep: nextStepIndex,
        };
      } else {
        // Tour completed naturally - update database and invalidate queries
        const currentTourId = prev.currentTourId;
        if (
          currentTourId === "overview" ||
          currentTourId === "tour-menu-intro"
        ) {
          markTourCompleted(session.profileId)
            .then((result) => {
              if (!result.success) {
                console.error(
                  "Failed to mark tour as completed:",
                  result.error,
                );
              }
            })
            .catch((error) => {
              console.error("Exception in nextStep:", error);
            });
        }

        invalidateQueriesForCurrentRoute();

        return {
          ...prev,
          isCompleted: true,
          isActive: false,
        };
      }
    });
  }, [session.profileId, invalidateQueriesForCurrentRoute]);

  const prevStep = useCallback(() => {
    setTourState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  const skipTour = useCallback(() => {
    setIsEndingTour(true);

    setTourState((prev) => {
      // Close sidebar and tags menu if it's the employees tour
      if (prev.currentTourId === "employees") {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("tour-close-employees-sidebar"));
          window.dispatchEvent(
            new CustomEvent("tour-close-employees-tags-menu"),
          );
        }, 50);
      }

      return {
        ...prev,
        isActive: false,
        isCompleted: true,
      };
    });

    // Check if user has been shown data import onboarding, if not show import intro modal
    if (!session.dataImportOnboardingShown && !importOnboardingDoneRef.current) {
      setTimeout(() => {
        setTourState((prev) => ({
          ...prev,
          showImportIntroModal: true,
        }));
      }, 100);
    }

    // Invalidate queries to refresh data
    setTimeout(() => {
      invalidateQueriesForCurrentRoute();
    }, 50);
  }, [invalidateQueriesForCurrentRoute, session.dataImportOnboardingShown]);

  const completeTour = useCallback(async () => {
    const currentTourId = tourState.currentTourId;

    setIsEndingTour(true);

    setTourState((prev) => {
      // Close sidebar and tags menu if it's the employees tour
      if (prev.currentTourId === "employees") {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("tour-close-employees-sidebar"));
          window.dispatchEvent(
            new CustomEvent("tour-close-employees-tags-menu"),
          );
        }, 50);
      }

      return {
        ...prev,
        isActive: false,
        isCompleted: true,
      };
    });

    // Mark tour as completed in local state
    if (currentTourId) {
      setCompletedTours((prev) => new Set(prev).add(currentTourId));
    }

    // Invalidate queries to refresh data
    setTimeout(() => {
      invalidateQueriesForCurrentRoute();
    }, 50);

    // Mark tour as completed in the database
    if (currentTourId === "overview" || currentTourId === "tour-menu-intro") {
      try {
        const result = await markTourCompleted(session.profileId);
        if (!result.success) {
          console.error("Failed to mark tour as completed:", result.error);
        }
      } catch (error) {
        console.error("Exception in completeTour:", error);
      }
    }

    // Check if user has been shown data import onboarding, if not show import intro modal
    if (!session.dataImportOnboardingShown && !importOnboardingDoneRef.current) {
      setTimeout(() => {
        setTourState((prev) => ({
          ...prev,
          showImportIntroModal: true,
        }));
      }, 100);
    }
  }, [
    tourState.currentTourId,
    session.profileId,
    session.dataImportOnboardingShown,
    invalidateQueriesForCurrentRoute,
  ]);

  const resetTour = useCallback(() => {
    setTourState({
      isActive: false,
      currentStep: 0,
      steps: [],
      isCompleted: false,
      showWelcomeModal: false,
      showImportIntroModal: false,
      showImportModal: false,
    });
  }, []);

  const resetEndingTour = useCallback(() => {
    setIsEndingTour(false);
  }, []);

  const closeWelcomeModal = useCallback(() => {
    setTourState((prev) => ({
      ...prev,
      showWelcomeModal: false,
    }));
  }, []);

  const startTourFromWelcome = useCallback(() => {
    setTourState((prev) => ({
      ...prev,
      showWelcomeModal: false,
    }));
    startTour("overview");
  }, [startTour]);

  const startAlternativeTour = useCallback(async () => {
    setTourState((prev) => ({
      ...prev,
      showWelcomeModal: false,
    }));

    // Mark tour as completed in database when user chooses "Ahora no"
    try {
      const { markTourCompleted } =
        await import("@/utils/serverActions/tourActions");
      const result = await markTourCompleted();

      if (result.success) {
        // Refresh session data to update tourCompleted status
        await refreshSession();
        console.log("✅ Tour marked as completed successfully");
      } else {
        console.error("❌ Failed to mark tour as completed:", result.error);
      }
    } catch (error) {
      console.error("Error updating tour completed status:", error);
    }

    // Check if user has been shown data import onboarding, if not show import intro modal
    if (!session.dataImportOnboardingShown && !importOnboardingDoneRef.current) {
      setTourState((prev) => ({
        ...prev,
        showImportIntroModal: true,
      }));
    }
  }, [session.dataImportOnboardingShown, refreshSession]);

  // After the import onboarding, offer the tour if it opened first.
  const offerWelcomeAfterImport = useCallback(() => {
    if (!offerWelcomeAfterImportRef.current) return;
    offerWelcomeAfterImportRef.current = false;
    if (!session.tourCompleted) {
      setTourState((prev) => ({ ...prev, showWelcomeModal: true }));
    }
  }, [session.tourCompleted]);

  const closeImportModal = useCallback(async () => {
    importOnboardingDoneRef.current = true;
    setTourState((prev) => ({
      ...prev,
      showImportModal: false,
    }));
    offerWelcomeAfterImport();

    // Mark data import onboarding as shown
    try {
      const { markDataImportOnboardingShown } =
        await import("@/utils/serverActions/tourActions");
      await markDataImportOnboardingShown();
      // Refresh session data
      queryClient.invalidateQueries({ queryKey: ["session"] });
    } catch (error) {
      console.error("Error marking data import onboarding as shown:", error);
    }
  }, [queryClient, offerWelcomeAfterImport]);

  const closeImportIntroModal = useCallback(async (next?: "import") => {
    importOnboardingDoneRef.current = true;
    setTourState((prev) => ({
      ...prev,
      showImportIntroModal: false,
      showImportModal: next === "import",
    }));
    if (next !== "import") offerWelcomeAfterImport();

    // Mark data import onboarding as shown
    try {
      const { markDataImportOnboardingShown } =
        await import("@/utils/serverActions/tourActions");
      await markDataImportOnboardingShown();
      // Refresh session data
      queryClient.invalidateQueries({ queryKey: ["session"] });
    } catch (error) {
      console.error("Error marking data import onboarding as shown:", error);
    }
  }, [queryClient, offerWelcomeAfterImport]);

  const showImportIntroModal = useCallback(() => {
    setTourState((prev) => ({
      ...prev,
      showImportIntroModal: true,
    }));
  }, []);

  const showImportModal = useCallback(() => {
    setTourState((prev) => ({
      ...prev,
      showImportModal: true,
    }));
  }, []);

  const getAvailableTours = useCallback(async (): Promise<TourDefinition[]> => {
    try {
      const { TOUR_DEFINITIONS } =
        await import("@/components/tour/tourDefinitions");
      return TOUR_DEFINITIONS.filter((tour) => !tour.hidden);
    } catch (error) {
      console.error("Error loading tour definitions:", error);
      return [];
    }
  }, []);

  const getTourProgress = useCallback(
    (tourId: string): boolean => {
      return completedTours.has(tourId);
    },
    [completedTours],
  );

  const isTourActive = useCallback((): boolean => {
    return tourState.isActive;
  }, [tourState.isActive]);

  // Expose isTourActive globally for scheduler components
  useEffect(() => {
    (window as unknown as { isTourActive: () => boolean }).isTourActive =
      isTourActive;
    return () => {
      delete (window as unknown as { isTourActive?: () => boolean })
        .isTourActive;
    };
  }, [isTourActive]);

  // Auto-launch tour for new users
  useEffect(() => {
    if (!pathname) {
      return;
    }

    // Only check once when session is fully loaded and user hasn't completed tour.
    // Skip auto-launch on mobile devices, shared links and transitional routes.
    const isLinksRoute =
      pathname.startsWith("/candidates/") ||
      pathname.startsWith("/customers/") ||
      pathname.startsWith("/services/") ||
      pathname.startsWith("/view/budget/") ||
      pathname.startsWith("/info/") ||
      pathname.startsWith("/invalid");
    const isBlockedRoute = isTourBlockedRoute(pathname);

    const canShowOnboarding =
      Boolean(session.userId) &&
      !hasAutoLaunched &&
      !tourState.isActive &&
      !isMobile &&
      !isLinksRoute &&
      !isBlockedRoute;

    // A company was just created: the import-or-start-empty choice comes first,
    // and the tour welcome after it (offerWelcomeAfterImport).
    if (
      canShowOnboarding &&
      window.sessionStorage.getItem(COMPANY_JUST_CREATED_KEY) === "1"
    ) {
      window.sessionStorage.removeItem(COMPANY_JUST_CREATED_KEY);
      setHasAutoLaunched(true);
      offerWelcomeAfterImportRef.current = true;
      setTourState((prev) => ({ ...prev, showImportIntroModal: true }));
      return;
    }

    if (
      session.userId &&
      !session.tourCompleted &&
      !hasAutoLaunched &&
      !tourState.isActive &&
      !tourState.isCompleted &&
      !isMobile &&
      !isLinksRoute &&
      !isBlockedRoute
    ) {
      setHasAutoLaunched(true);

      // Show welcome modal instead of starting tour directly
      setTourState((prev) => ({
        ...prev,
        showWelcomeModal: true,
      }));
    }
  }, [
    session.userId,
    session.tourCompleted,
    hasAutoLaunched,
    tourState.isActive,
    tourState.isCompleted,
    isMobile,
    pathname,
  ]);

  const value: TourContextType = {
    tourState,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    resetTour,
    getAvailableTours,
    getTourProgress,
    isTourActive,
    isEndingTour,
    resetEndingTour,
    closeWelcomeModal,
    startTourFromWelcome,
    startAlternativeTour,
    closeImportIntroModal,
    showImportIntroModal,
    closeImportModal,
    showImportModal,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
